import { useEffect, useMemo, useState, type FormEvent } from 'react';

import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import type { ApiResult, SupportRequestKind } from '../../lib/api';

export interface SupportRequestFormInput {
  kind: SupportRequestKind;
  message: string;
  contactEmail?: string;
}

interface SupportRequestDialogProps {
  open: boolean;
  sessionEmail: string | null;
  onClose: () => void;
  onSubmit: (
    input: SupportRequestFormInput,
  ) => Promise<ApiResult<{ success: true }>>;
}

const maxMessageLength = 2000;

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export function SupportRequestDialog({
  open,
  sessionEmail,
  onClose,
  onSubmit,
}: Readonly<SupportRequestDialogProps>): JSX.Element | null {
  const [kind, setKind] = useState<SupportRequestKind>('support');
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');
  const [error, setError] = useState<string | null>(null);

  const needsContactEmail = !sessionEmail;
  const messageCount = message.length;
  const isSubmitDisabled = useMemo(() => {
    if (status === 'submitting') {
      return true;
    }

    if (!message.trim()) {
      return true;
    }

    return needsContactEmail && !contactEmail.trim();
  }, [contactEmail, message, needsContactEmail, status]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setKind('support');
    setMessage('');
    setContactEmail('');
    setStatus('idle');
    setError(null);
  }, [open]);

  if (!open) {
    return null;
  }

  const handleClose = () => {
    if (status !== 'submitting') {
      onClose();
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedMessage = message.trim();
    const trimmedContactEmail = contactEmail.trim();

    if (!trimmedMessage) {
      setError('Tell us what happened.');
      return;
    }

    if (needsContactEmail && !isValidEmail(trimmedContactEmail)) {
      setError('Enter an email address so we can reply.');
      return;
    }

    setStatus('submitting');
    setError(null);

    const result = await onSubmit({
      kind,
      message: trimmedMessage,
      ...(needsContactEmail ? { contactEmail: trimmedContactEmail } : {}),
    });

    if (!result.success) {
      setStatus('idle');
      setError(result.error.message ?? result.error.error);
      return;
    }

    setStatus('sent');
  };

  return (
    <Dialog open={open}>
      <DialogOverlay onClick={handleClose} />
      <DialogContent role="dialog" aria-modal="true" aria-label="Contact support">
        {status === 'sent' ? (
          <div className="flex flex-col gap-4">
            <DialogHeader>
              <DialogTitle>Thanks - we got it.</DialogTitle>
              <DialogDescription>
                We will read your note and reply by email when a reply is needed.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={onClose}>
                Close
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Contact Support</DialogTitle>
              <DialogDescription>
                Send a short note. Basic diagnostics are attached automatically.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col gap-[0.35rem]">
              <Label htmlFor="support-request-kind">Request type</Label>
              <Select
                id="support-request-kind"
                value={kind}
                disabled={status === 'submitting'}
                onChange={(event) => {
                  setKind(event.target.value as SupportRequestKind);
                  setError(null);
                }}
              >
                <option value="support">Support</option>
                <option value="bug">Bug report</option>
                <option value="feature">Feature request</option>
              </Select>
            </div>

            {needsContactEmail ? (
              <div className="flex flex-col gap-[0.35rem]">
                <Label htmlFor="support-request-email">Email</Label>
                <Input
                  id="support-request-email"
                  value={contactEmail}
                  disabled={status === 'submitting'}
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  onChange={(event) => {
                    setContactEmail(event.target.value);
                    setError(null);
                  }}
                />
              </div>
            ) : (
              <p className="m-0 text-xs text-muted-foreground">
                We will reply to {sessionEmail}.
              </p>
            )}

            <div className="flex flex-col gap-[0.35rem]">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="support-request-message">Message</Label>
                <span className="text-[11px] text-muted-foreground">
                  {messageCount}/{maxMessageLength}
                </span>
              </div>
              <Textarea
                id="support-request-message"
                value={message}
                disabled={status === 'submitting'}
                maxLength={maxMessageLength}
                rows={5}
                placeholder="What happened? What were you trying to do?"
                onChange={(event) => {
                  setMessage(event.target.value);
                  setError(null);
                }}
                autoFocus
              />
            </div>

            {error ? (
              <p className="m-0 text-xs text-destructive" role="alert">
                {error}
              </p>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={status === 'submitting'}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitDisabled}>
                {status === 'submitting' ? 'Sending...' : 'Send'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
